// Lote E: Incidentes → Checklist del Anexo 1 para PO 9, PO 10, PO 12 y PO 13 (reglas en inc-estado.ts).
// Estado manual (misma regla que el Plan de Emergencias y las Inspecciones): un estado marcado a mano (auto_source NULL)
// nunca se sobrescribe; el Checklist muestra "el cálculo automático dice: X" y el botón "Usar cálculo automático".
// "No aplica" (sin casos en los últimos 12 meses): la base no deja que un cálculo lo escriba solo (M15: solo el
// administrador, con justificación), así que el sync no lo escribe; el Checklist lo muestra como cálculo y el botón lo
// aplica con la justificación del cálculo (la base exige que sea el administrador). Un "No aplica" guardado nunca lo
// cambia el sync: si el cálculo encuentra casos en el periodo, el ítem muestra la advertencia fuerte "Hay casos
// registrados en el periodo: revise este ítem" junto al botón (también arriba de la lista de Incidentes).
// Sin la M46 en la base (faltan las columnas nuevas) devuelve null y no toca nada.

import { supabase } from '../../supabaseClient';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazaItemPlan } from '../plan-emergencias-compliance-sync';
import { hoyIso } from './inc-plazos';
import {
  ITEMS_INCIDENTES_LOTE_E, resultadosIncidentes, type CasoInc, type EstadoCalculoInc, type ItemIncidentes, type MedidaInc,
  type ResultadoItemInc,
} from './inc-estado';

export const FUENTE_INCIDENTES_V2 = 'incidentes_aviso_medidas';
export const MODULO_INCIDENTES_V2 = 'Incidentes (aviso al IESS y medidas)';
export const COLUMNAS_CASO = 'id, incident_type, incident_date, iess_report_number, iess_report_date, iess_constancia_path';
export const COLUMNAS_MEDIDA_ESTADO = 'id, incident_id, status, due_date, evidence_path';

// Lo que muestra el Checklist (la forma de las trazas del plan, con "No aplica" como estado posible del cálculo).
export interface TrazaItemIncidentes extends Omit<TrazaItemPlan, 'estado'> {
  estado: EstadoCalculoInc;
  etiquetaGuardado?: string;   // reemplaza "Estado manual" cuando lo guardado no es un estado manual
  notaGuardado?: string;       // reemplaza "<módulo> no cambia un estado marcado a mano."
  observacion?: string | null;
  advertencia?: string;        // "No aplica" guardado con casos en el periodo: aviso fuerte junto al botón
}

export const ADVERTENCIA_NO_APLICA_CON_CASOS = 'Hay casos registrados en el periodo: revise este ítem';

/** ¿El ítem está guardado como "No aplica" (manual o confirmado desde el cálculo) y el cálculo encuentra casos? */
export const pideRevisarNoAplica = (existente: EstadoGuardadoChecklist | undefined, r: Pick<ResultadoItemInc, 'casos'>) =>
  existente?.estado === 'no_aplica' && r.casos > 0;

/** Ítems de Incidentes en "No aplica" con casos en el periodo (para el aviso de arriba de la lista de Incidentes). */
export function itemsNoAplicaConCasos(existentes: EstadoGuardadoChecklist[], res: Record<ItemIncidentes, Pick<ResultadoItemInc, 'casos'>>): ItemIncidentes[] {
  const porItem = new Map(existentes.map(e => [e.item_id, e]));
  return ITEMS_INCIDENTES_LOTE_E.filter(item => pideRevisarNoAplica(porItem.get(item), res[item]));
}

/** ¿Qué escribe el sync? null = nada. Nunca lo manual, nunca sobre "No aplica" y nunca "No aplica". */
export function decisionIncidentes(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoCalculoInc): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (deseado === 'no_aplica') return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_INCIDENTES_V2) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

const ETIQUETA_GUARDADO: Record<string, string> = { cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple', pendiente: 'Pendiente', no_aplica: 'No aplica' };

/** Cómo se presenta el ítem en el Checklist según lo guardado y lo calculado. */
export function presentacionIncidentes(existente: EstadoGuardadoChecklist | undefined, r: ResultadoItemInc): TrazaItemIncidentes {
  const base: TrazaItemIncidentes = {
    estado: r.estado, resumen: r.resumen, trazas: r.trazas, manualRespetado: false, estadoManual: null,
    modulo: MODULO_INCIDENTES_V2, fuente: FUENTE_INCIDENTES_V2, observacion: r.observacion,
  };
  const guardado = existente?.estado ?? 'pendiente';
  if (existente?.estado === 'no_aplica') {
    if (r.estado === 'no_aplica') return base;
    return { ...base, manualRespetado: true, estadoManual: 'no_aplica', etiquetaGuardado: 'Marcado como No aplica',
      notaGuardado: 'Un "No aplica" guardado no cambia solo: lo cambia el administrador.',
      ...(pideRevisarNoAplica(existente, r) ? { advertencia: ADVERTENCIA_NO_APLICA_CON_CASOS } : {}) };
  }
  if (esManual(existente)) {
    if (existente!.estado === r.estado) return base;
    return { ...base, manualRespetado: true, estadoManual: existente!.estado };
  }
  if (r.estado === 'no_aplica') {
    return { ...base, manualRespetado: true, estadoManual: guardado, etiquetaGuardado: `Estado guardado: ${ETIQUETA_GUARDADO[guardado] ?? guardado}`,
      notaGuardado: 'El cálculo no marca "No aplica" solo: lo confirma el administrador.' };
  }
  return base;
}

export async function syncIncidentesAvisoMedidas(companyId: string, hoy: Date = new Date()): Promise<Record<string, TrazaItemIncidentes> | null> {
  if (!companyId) return {};
  const [{ data: casos, error: e1 }, { data: medidas, error: e2 }, { data: existentes, error: e3 }] = await Promise.all([
    supabase.from('incidents').select(COLUMNAS_CASO).eq('company_id', companyId),
    supabase.from('incident_actions').select(COLUMNAS_MEDIDA_ESTADO).eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', [...ITEMS_INCIDENTES_LOTE_E]),
  ]);
  if (e1 || e2) return null;   // sin la M46 (faltan columnas) o sin permiso: no se toca nada
  if (e3) console.error('syncIncidentesAvisoMedidas: estados', e3);
  const res = resultadosIncidentes((casos ?? []) as CasoInc[], (medidas ?? []) as MedidaInc[], hoyIso(hoy));
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = ITEMS_INCIDENTES_LOTE_E.flatMap((item: ItemIncidentes) => {
    const d = decisionIncidentes(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_INCIDENTES_V2, updated_at: ahora }] : [];
  });
  if (filas.length && !e3) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncIncidentesAvisoMedidas', error);
  }
  return Object.fromEntries(ITEMS_INCIDENTES_LOTE_E.map(item => {
    const d = decisionIncidentes(porItem.get(item), res[item].estado);
    // Lo recién escrito ya es lo guardado.
    const existente = d && !e3 ? { item_id: item, estado: d, auto_source: FUENTE_INCIDENTES_V2 } : porItem.get(item);
    return [item, presentacionIncidentes(existente, res[item])];
  }));
}

export const justificacionNoAplica = (resumen: string) => `Cálculo del módulo Incidentes: ${resumen}`;

/**
 * "Usar cálculo automático": quita lo manual y escribe el cálculo con la fuente de Incidentes. "No aplica" se escribe
 * con la justificación del cálculo (solo el administrador, lo exige la base). Para salir de un "No aplica" guardado,
 * la base pide que el cambio no venga de un sync: primero se guarda el estado sin fuente y después se le pone la fuente.
 */
export async function aplicarCalculoIncidentes(companyId: string, itemId: string, estado: EstadoCalculoInc, resumen: string): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${itemId}: ${error.message}`);
  const actual = (data as { estado: string } | null)?.estado;
  const ahora = new Date().toISOString();
  const mensaje = (e: { code?: string; message: string }) => (e.code === '42501'
    ? (estado === 'no_aplica' || actual === 'no_aplica' ? 'Solo el administrador de la empresa puede marcar o quitar "No aplica".' : 'No tienes permiso para cambiar el Checklist.')
    : `No se pudo aplicar el cálculo en ${itemId}: ${e.message}`);
  if (estado === 'no_aplica') {
    const { data: u } = await supabase.auth.getUser();
    const { error: e } = await supabase.from('checklist_item_estados').upsert({
      company_id: companyId, item_id: itemId, estado: 'no_aplica', auto_source: FUENTE_INCIDENTES_V2, updated_at: ahora,
      no_aplica_justificacion: justificacionNoAplica(resumen), no_aplica_por: u?.user?.id ?? null, no_aplica_at: ahora,
    }, { onConflict: 'company_id,item_id' });
    if (e) throw new Error(mensaje(e));
    return;
  }
  if (actual === 'no_aplica') {
    const { error: e } = await supabase.from('checklist_item_estados').update({ estado, auto_source: null, updated_at: ahora })
      .eq('company_id', companyId).eq('item_id', itemId);
    if (e) throw new Error(mensaje(e));
  }
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_INCIDENTES_V2, updated_at: ahora },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(mensaje(e2));
}
